import { Component, Input, OnChanges, OnDestroy, OnInit } from '@angular/core';
import { FormBuilder, FormControl, FormGroup } from '@angular/forms';
import { firstValueFrom, Subject } from 'rxjs';
import { appearAnimations } from 'src/app/util/animations/appear-animations';
import { skeletonAnimations } from 'src/app/util/animations/skeleton-animations';
import { FmvChartsService } from './service/fmv-charts-backend.service';
import { FmvChart, FmvChartsPreData, PieChartCategory } from './pojo/fmv-charts-classes';
import { FairValueChartTabs, PpChartsMenuValues } from './pojo/fmv-charts-enums';
import { Organization } from '../../dto/Organization';
import { cloneDeep } from 'lodash';
import { FmvFilterService } from './service/filter-service/fmv-filter.service';
import { FairValueAndPpChartsOptionsService } from './service/charts-options-service/charts-options.service';
import { takeUntil } from 'rxjs/operators';

@Component({
  selector: 'app-fair-value-and-pp-charts',
  templateUrl: './fair-value-and-pp-charts.component.html',
  styleUrls: ['./fair-value-and-pp-charts.component.scss'],
  animations: [appearAnimations, skeletonAnimations],
})
export class FairValueAndPpChartsComponent implements OnInit, OnChanges, OnDestroy {
  private _destroyStream: Subject<void> = new Subject<void>();
  private previousFmvChartsPreData: FmvChartsPreData[] = []; // Store the previous value of fmvChartsPreData

  @Input() isOrgPage = false;
  @Input() organization: Organization;
  @Input() fmvChartsPreData: FmvChartsPreData[];
  filterForm: FormGroup;
  tab = FairValueChartTabs;
  tabNames = Object.values(FairValueChartTabs);
  selectedTab: FairValueChartTabs = FairValueChartTabs.COMPANY;
  chart = new FmvChart();
  originalChartData: FmvChart;
  dropdown = {
    show: false, // show or hide dropdown menu
    selectedValue: PpChartsMenuValues.fmv as PpChartsMenuValues, // dropdown selected items
  };

  show = {
    chart: true, // show or hide the whole component
    loadingState: true, // show or hide component skeleton
  };

  menuValues = PpChartsMenuValues;

  constructor(
    private fmvService: FmvChartsService,
    private formBuilder: FormBuilder) { }

  ngOnInit(): void {
    this.show.loadingState = true;
    this.initFilterForm();
    this.filterForm.valueChanges
      .pipe(takeUntil(this._destroyStream))
      .subscribe(() => {
        this.filterCharts();
      });
  }

  ngOnChanges(): void {
    this.tabNames = this.getTabNames();
    if (this.hasFmvChartsPreDataChanged() && this.fmvChartsPreData) {
      this.getChartsData();
    }
  }

  /**
   * Change tab.
   * Set filter values and recalculate data for charts
   */
  changeTab(tab: FairValueChartTabs): void {
    this.selectedTab = tab;
    this.cloneInitialData();
    this.changeFilterValue();
  }

  ngOnDestroy(): void {
    this._destroyStream.next();
    this._destroyStream.complete();
  }

  /**
   * Reset filter values after tab change to initial values
   */
  changeFilterValue(): void {
    switch (this.selectedTab) {
      case FairValueChartTabs.FUND:
        this.filterForm.controls.maxValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].max.byFund);
        this.filterForm.controls.minValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].min.byFund);
        break;
      case FairValueChartTabs.COMPANY:
        this.filterForm.controls.maxValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].max.byCompany);
        this.filterForm.controls.minValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].min.byCompany);
        break;
      case FairValueChartTabs.INDUSTRY:
        this.filterForm.controls.maxValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].max.byIndustry);
        this.filterForm.controls.minValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].min.byIndustry);
        break;
      case FairValueChartTabs.COUNTRY:
        this.filterForm.controls.maxValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].max.byCountry);
        this.filterForm.controls.minValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].min.byCountry);
        break;
      case FairValueChartTabs.GROUP:
        this.filterForm.controls.maxValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].max.byGroup);
        this.filterForm.controls.minValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].min.byGroup);
        break;
      case FairValueChartTabs.ASSETS:
        this.filterForm.controls.maxValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].max.byAssets);
        this.filterForm.controls.minValue.patchValue(this.chart.extremum[this.dropdown.selectedValue].min.byAssets);
        break;
    }
  }

  /**
   * Get chart data from backend and convert it in service in options for HighCharts.
   * Calculate extremum values for filters.
   */
  async getChartsData(): Promise<void> {
    this.show.loadingState = true;
    this.originalChartData = await firstValueFrom(this.fmvService
      .getFmvChartData(this.fmvChartsPreData)
      .pipe(takeUntil(this._destroyStream)));
    this.cloneInitialData();
    this.changeFilterValue();
    this.show.loadingState = false;
  }

  filterCharts(): void {
    this.cloneInitialData();
    const selectedMenu = this.dropdown.selectedValue;

    const minValue = this.filterForm.controls.minValue.value;
    const maxValue = this.filterForm.controls.maxValue.value;

    const filteredData = FmvFilterService.filterDataByKey(
      this.chart.data[selectedMenu],
      this.selectedTab.toLowerCase() as keyof PieChartCategory,
      minValue,
      maxValue,
      this.dropdown.selectedValue,
    );

    this.chart.options[selectedMenu] = FairValueAndPpChartsOptionsService.fillChartOptionsForType(filteredData, selectedMenu);
  }


  /**
   * Refresh filters by clicking on x and recalculate chart data
   */
  refreshFilterData(): void {
    this.changeFilterValue();
    this.filterForm.controls.minValue.patchValue(null);
    this.cloneInitialData();
  }

  /**
   * Hide or show charts and data behind table
   */
  toggleSelector(): void {
    this.dropdown.show = !this.dropdown.show;
  }

  /**
   * Change chart type for Investment or Fair value and make recalculations.
   *
   * @param chartType chart type
   */
  selectMenuItem(chartType: PpChartsMenuValues): void {
    this.dropdown.selectedValue = chartType;
    this.dropdown.show = false;
    this.cloneInitialData();
    this.changeFilterValue();
  }

  private initFilterForm(): void {
    this.filterForm = this.formBuilder.group({
      minValue: new FormControl<number>(-Number.MAX_VALUE),
      maxValue: new FormControl<number>(Number.MAX_VALUE),
    });
  }

  private cloneInitialData(): void {
    this.chart = cloneDeep(this.originalChartData);
  }

  private getTabNames(): FairValueChartTabs[] {
    const allTabs = Object.values(FairValueChartTabs);
    if (!this.isOrgPage) {
      return allTabs.filter((tab) => tab !== FairValueChartTabs.FUND);
    }
    return allTabs;
  }

  private hasFmvChartsPreDataChanged(): boolean {
    const hasChanged = JSON.stringify(this.fmvChartsPreData) !== JSON.stringify(this.previousFmvChartsPreData);
    if (hasChanged) {
      this.previousFmvChartsPreData = JSON.parse(JSON.stringify(this.fmvChartsPreData));
    }
    return hasChanged;
  }
}


